import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../../components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { AdCard, useAds } from '../../components/ads/AdCard';
import { useFeatures } from '../../contexts/FeatureContext';
import { 
  ShoppingBag, 
  TrendingUp, 
  MapPin, 
  Sparkles,
  ChevronRight,
  Ticket,
  Package,
  Megaphone,
  Store,
  Truck,
  Briefcase,
  Users
} from 'lucide-react';

export const DiscoverPage = () => {
  const navigate = useNavigate();
  const { ads, loading } = useAds('offers');
  const { isFeatureEnabled } = useFeatures();

  const sections = [
    {
      title: 'Commerce',
      icon: Store,
      color: 'text-pink-600',
      bgColor: 'bg-pink-50 dark:bg-pink-950',
      items: [
        { label: 'MTR Marketplace', path: '/commerce/marketplace', icon: Store, badge: 'New', feature: 'marketplace' },
        { label: 'My Store', path: '/commerce/dashboard', icon: Package, feature: 'merchant_dashboard' },
        { label: 'My Orders', path: '/commerce/orders', icon: ShoppingBag, feature: 'checkout' },
        { label: 'My Vouchers', path: '/user/vouchers', icon: Ticket, feature: 'vouchers' },
      ]
    },
    {
      title: 'Matrise Runners',
      icon: Truck,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50 dark:bg-blue-950',
      items: [
        { label: 'Matrise Runners Home', path: '/pickup', icon: Truck, badge: 'Popular', feature: 'runners_service' },
        { label: 'Browse Jobs', path: '/pickup/browse', icon: MapPin, feature: 'runners_service' },
        { label: 'Find a Runner', path: '/pickup/find-runner', icon: Users, feature: 'runners_service' },
        { label: 'Post a Job', path: '/pickup/post', icon: Package, feature: 'runners_service' },
        { label: 'My Jobs', path: '/pickup/my-jobs', icon: Briefcase, feature: 'runners_service' },
      ]
    },
    {
      title: 'Predictions',
      icon: TrendingUp,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50 dark:bg-indigo-950',
      items: [
        { label: 'Pool Predictions', path: '/app/predictions', icon: TrendingUp, badge: 'New', feature: 'predictions' },
      ]
    },
    {
      title: 'Services',
      icon: MapPin,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50 dark:bg-emerald-950',
      items: [
        { label: 'Find Agents Near Me', path: '/app/discover/agents', icon: MapPin, badge: 'New', feature: 'agent_network' },
      ]
    },
    {
      title: 'Mini Apps',
      icon: Sparkles,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50 dark:bg-purple-950',
      items: [
        { label: 'Matrise Ads - Advertising Platform', path: '/discover/mini-apps/matrise-ads', icon: Megaphone, badge: 'New', feature: 'ads_platform' },
      ]
    },
  ];

  // Filter sections and items by feature availability
  const filteredSections = sections
    .map(section => ({
      ...section,
      items: section.items.filter(item => !item.feature || isFeatureEnabled(item.feature))
    }))
    .filter(section => section.items.length > 0);

  return (
    <AppLayout>
      <div className="p-4 space-y-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Discover</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Explore ecosystem services and marketplace
          </p>
        </div>

        {/* Featured Banner */}
        <Card className="bg-gradient-to-br from-purple-600 to-pink-600 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-5 w-5" />
                  <span className="text-sm font-semibold">Featured</span>
                </div>
                <h3 className="text-xl font-bold mb-2">New Agent Services</h3>
                <p className="text-sm opacity-90 mb-4">
                  Find nearby agents for cash deposits and withdrawals
                </p>
                <button
                  onClick={() => navigate('/app/discover/agents')}
                  className="px-4 py-2 bg-white text-purple-600 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-colors"
                >
                  Explore Now
                </button>
              </div>
              <MapPin className="h-12 w-12 opacity-30" />
            </div>
          </CardContent>
        </Card>

        {/* Sections */}
        <div className="space-y-4">
          {filteredSections.map((section) => {
            const SectionIcon = section.icon;
            return (
              <Card key={section.title}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className={`${section.bgColor} p-2 rounded-lg`}>
                      <SectionIcon className={`h-5 w-5 ${section.color}`} />
                    </div>
                    <CardTitle className="text-lg">{section.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {section.items.map((item) => {
                    const ItemIcon = item.icon;
                    return (
                      <button
                        key={item.label}
                        onClick={() => navigate(item.path)}
                        className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <ItemIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {item.label}
                          </span>
                          {item.badge && (
                            <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs font-semibold rounded-full">
                              {item.badge}
                            </span>
                          )}
                        </div>
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </button>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Ads Section (bottom) */}
        {!loading && ads.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="h-4 w-4" />
              <span>Sponsored Content</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ads.slice(0, 3).map((ad) => (
                <AdCard key={ad.id} ad={ad} />
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};
